// Remaining application views: admin, reports, self-service,
// salesforce, lawyer workbench, metrics, custom dashboards.
//
// This file holds the bulk of the per-role landing pages and
// admin-only management UIs. Future sessions may split this further
// (e.g. admin/, reports/) but for now they live together as the
// last extraction batch before the App entry point.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { T } from "../theme";
import { fmtDate, today, capitalize } from "../utils";
import { apiCall } from "../apiClient";
import { useClientScope } from "../contexts/ClientScopeContext";
import { DEPARTMENTS, STATUS_COLORS } from "../constants";
import { SF_INSTANCE, SF_OPPORTUNITIES, SF_STAGE_COLORS, sfLink } from "../salesforce";
import { renderMarkdown } from "./markdown";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";
import { DashboardView } from "./Dashboards";
import { ExtractionDashView, SecurityView, ValidationView } from "./AiViews";
import { SsoProvidersView } from "./SsoProvidersView";

const KNOWN_FIELDS = ['Renewal Clause','Notice Period','Payment Terms','Limitation of Liability Summary','Indemnification Summary','Confidentiality Term','Assignment Clause','Termination Rights','Insurance Requirements','Dispute Resolution','License Scope','License Restrictions','IP Ownership','Service Level Requirements','Reference to Parent Agreement','Scope of Services','Deliverables','Fee Structure','Payment Schedule','Project Timeline','Milestones','Acceptance Criteria','Key Personnel','Sections Modified','Revised Terms','Amendment Effective Date','Restates Entire Agreement','Definition of Confidential Information','Permitted Disclosures','Return or Destruction of Information','Mutual or One-Way','Data Categories','Processing Purposes','Data Subject Rights','Sub-processor Requirements','Data Breach Notification Period','Data Transfer Mechanisms','Data Retention Period'];

export const ExtractionProfileManager = () => {
  const [profiles, setProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<any>(null); // profile id or 'new'
  const [form, setForm] = useState<Record<string, any>>({ name: '', description: '', fields: [] });
  const [customField, setCustomField] = useState('');
  const [saving, setSaving] = useState(false);

  // Tenant-configurable universal + verbatim fields. Loaded once on
  // mount; new profiles pre-select the universal set, existing profiles
  // get a "Universal" badge on any field that is in this list.
  const [universalFields, setUniversalFields] = useState<string[]>([]);
  const [verbatimFields, setVerbatimFields] = useState<string[]>([]);
  const [universalDefaults, setUniversalDefaults] = useState<string[]>([]);
  const [verbatimDefaults, setVerbatimDefaults] = useState<string[]>([]);
  const [universalDraft, setUniversalDraft] = useState<string[]>([]);
  const [verbatimDraft, setVerbatimDraft] = useState<string[]>([]);
  const [newUniversalEntry, setNewUniversalEntry] = useState('');
  const [newVerbatimEntry, setNewVerbatimEntry] = useState('');
  const [universalEditOpen, setUniversalEditOpen] = useState(false);
  const [universalSaving, setUniversalSaving] = useState(false);
  const [universalMsg, setUniversalMsg] = useState('');

  const loadUniversal = async () => {
    const r = await apiCall('/settings/extraction-fields');
    if (r?.universalFields) {
      setUniversalFields(r.universalFields);
      setUniversalDraft(r.universalFields);
    }
    if (r?.verbatimFields) {
      setVerbatimFields(r.verbatimFields);
      setVerbatimDraft(r.verbatimFields);
    }
    if (r?.defaults) {
      setUniversalDefaults(r.defaults.universalFields || []);
      setVerbatimDefaults(r.defaults.verbatimFields || []);
    }
  };

  const load = async () => {
    setLoading(true);
    const r = await apiCall('/extraction/extraction-profiles');
    if (r?.profiles) setProfiles(r.profiles);
    setLoading(false);
  };
  useEffect(() => { load(); loadUniversal(); }, []);

  const startEdit = (p: any) => {
    const fields = typeof p.fields === 'string' ? JSON.parse(p.fields) : (p.fields || []);
    setForm({ name: p.name, description: p.description || '', fields: [...fields] });
    setEditing(p.id);
  };
  // Pre-select the universal field set on new profiles. Operator can
  // uncheck individual entries — universal is a starting point, not locked.
  const startNew = () => { setForm({ name: '', description: '', fields: [...universalFields] }); setEditing('new'); };
  const cancel = () => { setEditing(null); setForm({ name: '', description: '', fields: [] }); setCustomField(''); };

  // ── Universal-fields editor helpers ──
  const addUniversalDraft = () => {
    const v = newUniversalEntry.trim();
    if (!v || universalDraft.includes(v)) return;
    setUniversalDraft([...universalDraft, v]);
    setNewUniversalEntry('');
  };
  const removeUniversalDraft = (f: string) => setUniversalDraft(universalDraft.filter(x => x !== f));
  const addVerbatimDraft = () => {
    const v = newVerbatimEntry.trim();
    if (!v || verbatimDraft.includes(v)) return;
    setVerbatimDraft([...verbatimDraft, v]);
    setNewVerbatimEntry('');
  };
  const removeVerbatimDraft = (f: string) => setVerbatimDraft(verbatimDraft.filter(x => x !== f));
  const resetUniversalToDefaults = () => {
    setUniversalDraft([...universalDefaults]);
    setVerbatimDraft([...verbatimDefaults]);
    setUniversalMsg('Reset to defaults — click Save to apply.');
  };
  const saveUniversal = async () => {
    setUniversalSaving(true); setUniversalMsg('');
    const r = await apiCall('/settings/extraction-fields', {
      method: 'PUT',
      body: JSON.stringify({ universalFields: universalDraft, verbatimFields: verbatimDraft }),
    });
    setUniversalSaving(false);
    if (r?.ok) {
      setUniversalFields(r.universalFields || universalDraft);
      setVerbatimFields(r.verbatimFields || verbatimDraft);
      setUniversalMsg('Saved. New extractions will use these fields immediately.');
      setUniversalEditOpen(false);
    } else {
      setUniversalMsg(r?.error || 'Save failed');
    }
  };

  const toggleField = (f: string) => {
    setForm((prev: any) => ({ ...prev, fields: prev.fields.includes(f) ? prev.fields.filter((x: any) => x !== f) : [...prev.fields, f] }));
  };
  const addCustom = () => {
    const f = customField.trim();
    if (f && !form.fields.includes(f)) { setForm((prev: any) => ({ ...prev, fields: [...prev.fields, f] })); setCustomField(''); }
  };

  const save = async () => {
    setSaving(true);
    let r;
    if (editing === 'new') {
      r = await apiCall('/extraction/extraction-profiles', { method: 'POST', body: JSON.stringify(form) });
    } else {
      r = await apiCall(`/extraction/extraction-profiles/${editing}`, { method: 'PUT', body: JSON.stringify(form) });
    }
    setSaving(false);
    if (r?.profile) { cancel(); load(); } else { alert(r?.error || 'Save failed'); }
  };

  const deleteProfile = async (id: string) => {
    if (!window.confirm('Delete this extraction profile?')) return;
    const r = await apiCall(`/extraction/extraction-profiles/${id}`, { method: 'DELETE' });
    if (r) load(); else alert(r?.error || 'Delete failed');
  };

  if (loading) return <div style={{ padding: 20, color: T.txD }}>Loading profiles...</div>;

  const isUniversal = (f: string) => universalFields.includes(f);

  return (
    <div>
      {/* ─── Universal Fields editor ─── */}
      <Card style={{ padding: 14, marginBottom: 16, border: `1px solid ${T.brd}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.tx }}>Universal Fields</div>
            <div style={{ fontSize: 10, color: T.txD, marginTop: 2 }}>Extracted on every contract regardless of type. Pre-selected on new profiles.</div>
          </div>
          {!universalEditOpen ? (
            <Btn v="ghost" sz="sm" onClick={() => { setUniversalDraft([...universalFields]); setVerbatimDraft([...verbatimFields]); setUniversalEditOpen(true); }}>Edit</Btn>
          ) : (
            <div style={{ display: 'flex', gap: 6 }}>
              <Btn v="ghost" sz="sm" onClick={resetUniversalToDefaults}>Reset to defaults</Btn>
              <Btn v="ghost" sz="sm" onClick={() => { setUniversalDraft([...universalFields]); setVerbatimDraft([...verbatimFields]); setUniversalEditOpen(false); setUniversalMsg(''); }}>Cancel</Btn>
              <Btn v="primary" sz="sm" onClick={saveUniversal} disabled={universalSaving || universalDraft.length === 0}>{universalSaving ? 'Saving...' : 'Save'}</Btn>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {(universalEditOpen ? universalDraft : universalFields).map(f => (
            <span key={f} style={{ padding: '4px 10px', fontSize: 11, borderRadius: 12, border: `1px solid ${T.blu}`, backgroundColor: T.bluS, color: T.blu, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {f}
              {universalEditOpen && <span onClick={() => removeUniversalDraft(f)} style={{ cursor: 'pointer', opacity: 0.7 }}>x</span>}
            </span>
          ))}
        </div>
        {universalEditOpen && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input value={newUniversalEntry} onChange={e => setNewUniversalEntry(e.target.value)} onKeyDown={e => e.key === 'Enter' && addUniversalDraft()} placeholder="Add universal field..." style={{ flex: 1, padding: '6px 10px', border: `1px solid ${T.brd}`, borderRadius: 5, fontSize: 11, backgroundColor: T.bg3, color: T.tx, outline: 'none', fontFamily: 'inherit' }} />
            <Btn v="secondary" sz="sm" onClick={addUniversalDraft} disabled={!newUniversalEntry.trim()}>Add</Btn>
          </div>
        )}
        <div style={{ fontSize: 10, color: T.txD, textTransform: 'uppercase', letterSpacing: '.04em', marginTop: 10, marginBottom: 6 }}>Verbatim Variants ({(universalEditOpen ? verbatimDraft : verbatimFields).length})</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {(universalEditOpen ? verbatimDraft : verbatimFields).map(f => (
            <span key={f} style={{ padding: '3px 8px', fontSize: 10, borderRadius: 10, border: `1px solid ${T.brd}`, color: T.txM, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {f}
              {universalEditOpen && <span onClick={() => removeVerbatimDraft(f)} style={{ cursor: 'pointer', opacity: 0.7 }}>x</span>}
            </span>
          ))}
          {(universalEditOpen ? verbatimDraft : verbatimFields).length === 0 && <span style={{ fontSize: 10, color: T.txD, fontStyle: 'italic' }}>(none — verbatim variants disabled)</span>}
        </div>
        {universalEditOpen && (
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={newVerbatimEntry} onChange={e => setNewVerbatimEntry(e.target.value)} onKeyDown={e => e.key === 'Enter' && addVerbatimDraft()} placeholder="Add verbatim variant (e.g. Effective Date Verbatim)..." style={{ flex: 1, padding: '6px 10px', border: `1px solid ${T.brd}`, borderRadius: 5, fontSize: 11, backgroundColor: T.bg3, color: T.tx, outline: 'none', fontFamily: 'inherit' }} />
            <Btn v="secondary" sz="sm" onClick={addVerbatimDraft} disabled={!newVerbatimEntry.trim()}>Add</Btn>
          </div>
        )}
        {universalMsg && <div style={{ fontSize: 10, color: T.txD, marginTop: 8 }}>{universalMsg}</div>}
        <div style={{ fontSize: 10, color: T.txD, marginTop: 8 }}>
          Defaults: {universalDefaults.length} universal, {verbatimDefaults.length} verbatim. Currently {universalFields.length === universalDefaults.length && universalFields.every((f, i) => f === universalDefaults[i]) ? 'using defaults' : 'customized'}.
        </div>
      </Card>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>Extraction Profiles</div>
        <Btn v="primary" sz="sm" onClick={startNew}>+ New Profile</Btn>
      </div>

      {editing && (
        <Card style={{ padding: 16, marginBottom: 16, border: `2px solid ${T.acc}` }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.tx, marginBottom: 12 }}>{editing === 'new' ? 'New Profile' : 'Edit Profile'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 10, color: T.txD, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 }}>Name</div>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={{ width: '100%', padding: '8px 10px', border: `1px solid ${T.brd}`, borderRadius: 5, fontSize: 12, backgroundColor: T.bg3, color: T.tx, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
            </div>
            <div>
              <div style={{ fontSize: 10, color: T.txD, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 }}>Description</div>
              <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ width: '100%', padding: '8px 10px', border: `1px solid ${T.brd}`, borderRadius: 5, fontSize: 12, backgroundColor: T.bg3, color: T.tx, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
            </div>
          </div>
          <div style={{ fontSize: 10, color: T.txD, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>Fields ({form.fields.length} selected{form.fields.filter((f: any) => isUniversal(f)).length > 0 ? ` · ${form.fields.filter((f: any) => isUniversal(f)).length} universal` : ''})</div>
          {/* Universal Fields group — always extracted on every contract */}
          {universalFields.length > 0 && (
            <>
              <div style={{ fontSize: 10, color: T.txD, marginBottom: 4 }}>Universal Fields (always extracted)</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {universalFields.map(f => {
                  const sel = form.fields.includes(f);
                  return (
                    <span key={f} onClick={() => toggleField(f)} title="Universal field" style={{ padding: '4px 10px', fontSize: 11, borderRadius: 12, cursor: 'pointer', border: `1px solid ${T.blu}`, backgroundColor: sel ? T.bluS : 'transparent', color: T.blu, fontWeight: sel ? 600 : 500, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 8, padding: '1px 4px', borderRadius: 3, backgroundColor: T.blu, color: '#fff', fontWeight: 700, letterSpacing: '.05em' }}>U</span>
                      {f}
                    </span>
                  );
                })}
              </div>
            </>
          )}
          {/* Type-specific fields */}
          <div style={{ fontSize: 10, color: T.txD, marginBottom: 4 }}>Type-Specific Fields</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {KNOWN_FIELDS.filter(f => !isUniversal(f)).map(f => (
              <span key={f} onClick={() => toggleField(f)} style={{ padding: '4px 10px', fontSize: 11, borderRadius: 12, cursor: 'pointer', border: `1px solid ${form.fields.includes(f) ? T.acc : T.brd}`, backgroundColor: form.fields.includes(f) ? T.accS : 'transparent', color: form.fields.includes(f) ? T.acc : T.txM, fontWeight: form.fields.includes(f) ? 600 : 400 }}>{f}</span>
            ))}
            {form.fields.filter((f: any) => !KNOWN_FIELDS.includes(f) && !isUniversal(f)).map((f: any) => (
              <span key={f} onClick={() => toggleField(f)} style={{ padding: '4px 10px', fontSize: 11, borderRadius: 12, cursor: 'pointer', border: `1px solid ${T.prp}`, backgroundColor: T.prpS, color: T.prp, fontWeight: 600 }}>{f} x</span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input value={customField} onChange={e => setCustomField(e.target.value)} onKeyDown={e => e.key === 'Enter' && addCustom()} placeholder="Add custom field..." style={{ flex: 1, padding: '6px 10px', border: `1px solid ${T.brd}`, borderRadius: 5, fontSize: 11, backgroundColor: T.bg3, color: T.tx, outline: 'none', fontFamily: 'inherit' }} />
            <Btn v="secondary" sz="sm" onClick={addCustom} disabled={!customField.trim()}>Add</Btn>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn v="ghost" sz="sm" onClick={cancel}>Cancel</Btn>
            <Btn v="primary" sz="sm" onClick={() => { save(); }} disabled={saving || !form.name.trim() || form.fields.length === 0}>{saving ? 'Saving...' : 'Save Profile'}</Btn>
          </div>
        </Card>
      )}

      {profiles.map(p => {
        const fields = typeof p.fields === 'string' ? JSON.parse(p.fields) : (p.fields || []);
        return (
          <Card key={p.id} style={{ padding: 14, marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.tx }}>{p.name}</span>
                {p.is_system && <Badge c={T.blu} b={T.bluS}>System</Badge>}
                <span style={{ fontSize: 11, color: T.txD }}>{p.description}</span>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <Btn v="ghost" sz="sm" onClick={() => startEdit(p)}>Edit</Btn>
                {!p.is_system && <Btn v="ghost" sz="sm" onClick={() => deleteProfile(p.id)} style={{ color: T.red }}>Delete</Btn>}
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {fields.map((f: any) => {
                const u = isUniversal(f);
                return u ? (
                  <span key={f} title="Universal field" style={{ padding: '2px 8px', fontSize: 10, borderRadius: 10, border: `1px solid ${T.blu}`, backgroundColor: T.bluS, color: T.blu, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 7, padding: '1px 3px', borderRadius: 2, backgroundColor: T.blu, color: '#fff', fontWeight: 700, letterSpacing: '.05em' }}>U</span>{f}
                  </span>
                ) : (
                  <span key={f} style={{ padding: '2px 8px', fontSize: 10, borderRadius: 10, border: `1px solid ${T.brd}`, color: T.txM }}>{f}</span>
                );
              })}
              {/* Universal fields not currently in the profile — quick add */}
              {universalFields.filter(u => !fields.includes(u)).map(u => (
                <span key={`miss-${u}`} title="Universal field not yet in this profile" onClick={async () => { await apiCall(`/extraction/extraction-profiles/${p.id}`, { method: 'PUT', body: JSON.stringify({ name: p.name, description: p.description || '', fields: [...fields, u] }) }); load(); }} style={{ padding: '2px 8px', fontSize: 10, borderRadius: 10, border: `1px dashed ${T.blu}`, color: T.blu, opacity: 0.7, cursor: 'pointer' }}>+ {u}</span>
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
};

/* ═══════════════════ BACKUP STATUS ═══════════════════ */
